import React, { useMemo, useState } from 'react';
import Icon from './Icon';
import { managerInitials } from './RelationshipManagersField';
import { OUTREACH_STAGE_LABELS } from './utils';

const COLUMN_ORDER = ['met', 'in_progress', 'contacted', 'not_contacted', 'declined'];
// Almost every account sits in "not contacted" (94% in practice) and "declined" isn't
// actionable, so both start collapsed to a handful instead of flooding the column.
const COLLAPSED_LIMIT = { not_contacted: 3, declined: 0 };

const TIER_RANK = { S: 0, A: 1, B: 2, C: 3 };

// Where a card sorts before anyone has dragged it: by tier, then its place on the ICP workbook's
// list - spaced widely so a dragged card's real kanban_position always has room to land between
// any two of them. Once a card is dragged, its own kanban_position takes over.
const effectivePosition = (account) => (account.kanban_position != null
  ? account.kanban_position
  : ((TIER_RANK[account.tier] ?? 99) * 1_000_000) + (account.list_rank ?? 999_999));

// A slot the size of a card, shown where a dragged card would land if dropped now.
const DropPlaceholder = () => <div className="pipeline-kanban__placeholder" aria-hidden="true" />;

const AccountCard = ({ account, nextId, dragging, onOpenCompany, onDragStart, onDragEnd, onHover }) => {
  const open = () => onOpenCompany({ id: account.company_id, name: account.company_name });

  return (
    <div
      className={`pipeline-kanban__card${dragging ? ' pipeline-kanban__card--dragging' : ''}`}
      tabIndex={0}
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onDragOver={(event) => {
        event.preventDefault();
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        const before = event.clientY < rect.top + rect.height / 2;
        onHover(before ? account.id : nextId);
      }}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter') open();
      }}
    >
      <div className="pipeline-kanban__card-top">
        <span className="pipeline-kanban__card-name">{account.company_name}</span>
        {account.pending_findings_count > 0 && (
          <span className="pipeline-kanban__pending" title={`${account.pending_findings_count} finding(s) awaiting review`}>
            <Icon name="alert-triangle" /> {account.pending_findings_count}
          </span>
        )}
      </div>
      <div className="pipeline-kanban__card-meta">
        {account.tier && <span className="pipeline-kanban__tier" data-tier={account.tier}>Tier {account.tier}</span>}
        {account.icp_rank != null && <span>ICP {account.icp_rank}</span>}
        {account.target_people_count > 0 && (
          <span className="pipeline-kanban__people"><Icon name="user-check" /> {account.target_people_count}</span>
        )}
      </div>
      {account.relationship_managers?.length > 0 && (
        <div className="pipeline-kanban__rms">
          {account.relationship_managers.map(manager => (
            <span key={manager.name} className="rm-avatar pipeline-kanban__avatar" title={manager.name} aria-hidden="true">
              {managerInitials(manager.name)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Target accounts as a Kanban board by outreach stage - the other option from the IA design
 * assessment (the user picked the sortable table first; this fills in the one left undecided).
 * Takes PipelineView's already name-filtered accounts; only groups, collapses and lays them out.
 *
 * Cards are draggable, within a column to reorder it and across columns to change its stage. This
 * is the one list in the app where the user's own order wins over any automatic sort: a dragged
 * card's kanban_position is a midpoint between its new neighbors (effectivePosition below), and
 * once set it stays put instead of snapping back to the tier/list_rank order on the next load.
 * A placeholder tracks the drop slot live, so it's clear where letting go will put the card.
 */
const PipelineKanban = ({ accounts, hiddenStages, onOpenCompany, onMoveAccount }) => {
  const [openStages, setOpenStages] = useState({});
  const [draggedId, setDraggedId] = useState(null);
  // { stage, beforeId } - beforeId is an account's id, or null for "at the end of this column".
  // null (no object) means nothing is being dragged over a column right now.
  const [dropTarget, setDropTarget] = useState(null);

  const byStage = useMemo(() => {
    const groups = {};
    COLUMN_ORDER.forEach(stage => { groups[stage] = []; });
    accounts.forEach(account => {
      const stage = account.outreach_stage || 'not_contacted';
      (groups[stage] || (groups[stage] = [])).push(account);
    });
    Object.values(groups).forEach(list => list.sort((a, b) => effectivePosition(a) - effectivePosition(b)));
    return groups;
  }, [accounts]);

  const columns = COLUMN_ORDER.filter(stage => !hiddenStages.includes(stage));
  const destListFor = (stage) => (byStage[stage] || []).filter(a => a.id !== draggedId);

  const clearDrag = () => {
    setDraggedId(null);
    setDropTarget(null);
  };

  const hoverTarget = (stage, beforeId) => {
    setDropTarget(current => (current?.stage === stage && current?.beforeId === beforeId
      ? current
      : { stage, beforeId }));
  };

  const drop = () => {
    const dragged = accounts.find(a => a.id === draggedId);
    const target = dropTarget;
    clearDrag();
    if (!dragged || !target) return;
    const destList = destListFor(target.stage);
    const index = target.beforeId == null ? destList.length : destList.findIndex(a => a.id === target.beforeId);
    const prev = destList[index - 1];
    const next = destList[index];
    const prevPos = prev ? effectivePosition(prev) : (next ? effectivePosition(next) - 2 : 0);
    const nextPos = next ? effectivePosition(next) : prevPos + 2;
    const changes = { kanban_position: (prevPos + nextPos) / 2 };
    if (dragged.outreach_stage !== target.stage) changes.outreach_stage = target.stage;
    onMoveAccount(dragged.id, changes);
  };

  return (
    <div className="pipeline-kanban">
      {columns.map(stage => {
        const list = byStage[stage] || [];
        const limit = COLLAPSED_LIMIT[stage];
        const collapsed = limit !== undefined && list.length > limit && !openStages[stage];
        const visible = collapsed ? list.slice(0, limit) : list;
        const hiddenCount = list.length - visible.length;
        const targetHere = dropTarget?.stage === stage ? dropTarget : null;

        return (
          <div
            className={`pipeline-kanban__column${targetHere ? ' pipeline-kanban__column--drag-over' : ''}`}
            key={stage}
            onDragOver={(event) => {
              event.preventDefault();
              hoverTarget(stage, null);
            }}
            onDrop={(event) => {
              event.preventDefault();
              drop();
            }}
          >
            <div className="pipeline-kanban__column-head">
              <span>{OUTREACH_STAGE_LABELS[stage]}</span>
              <span className="pipeline-kanban__column-count">{list.length}</span>
            </div>
            <div className="pipeline-kanban__column-body">
              {visible.map((account, index) => (
                <React.Fragment key={account.id}>
                  {targetHere?.beforeId === account.id && <DropPlaceholder />}
                  <AccountCard
                    account={account}
                    nextId={visible[index + 1]?.id ?? null}
                    dragging={draggedId === account.id}
                    onOpenCompany={onOpenCompany}
                    onDragStart={() => setDraggedId(account.id)}
                    onDragEnd={clearDrag}
                    onHover={(beforeId) => hoverTarget(stage, beforeId)}
                  />
                </React.Fragment>
              ))}
              {targetHere?.beforeId === null && <DropPlaceholder />}
              {hiddenCount > 0 && (
                <button
                  type="button"
                  className="pipeline-kanban__more"
                  onClick={() => setOpenStages(current => ({ ...current, [stage]: true }))}
                >
                  +{hiddenCount} more, sorted by tier
                </button>
              )}
              {list.length === 0 && <p className="placeholder-text pipeline-kanban__empty">None</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PipelineKanban;
